import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  AuthState,
  LoginCredentials,
  LoginResponse,
  ChangePasswordRequest,
  PasswordResetRequest,
  PasswordResetVerification,
  TwoFactorVerification,
  TwoFactorSetup,
  RoleCheck
} from '../types/auth';
import { User, Permission, UserRole, Department } from '../types/user';
import type {
  RequiresTenantPick,
  TenantMembershipInfo,
  TenantSessionInfo,
} from '../types/tenant';
import apiClient, { performTokenRefresh } from '../services/apiClient';
import { onTokensRefreshed } from '../services/authBus';
import {
  clearRememberedTenant,
  rememberLastTenant,
} from '../services/tenantApi';

type BackendTokenResponse = {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive?: boolean;
    lastLogin?: string;
    createdAt?: string;
    updatedAt?: string;
    role: {
      id: string;
      name: string;
      permissions: Array<{ name: string }>;
    };
    /**
     * The EXTRA roles this one account also holds — `g_m_addp`'s additive
     * switches. `role.permissions` above is ALREADY the union across all of
     * them; this is what lets `hasRole` answer "is she also a nurse" without a
     * second call. Absent on an older backend, which reads as "just the one".
     */
    additionalRoles?: string[];
  };
  tenant?: TenantSessionInfo;
  passwordExpired?: boolean;
};

type BackendLoginEnvelope =
  | { success: true; data: BackendTokenResponse }
  | { success: true; data: RequiresTenantPick };

export interface SignUpPayload {
  email: string;
  password: string;
  acceptedTerms: boolean;
  clinicName?: string;
  slug?: string;
  firstName?: string;
  lastName?: string;
}

interface AuthStore extends AuthState {
  // Tenant state
  activeTenantId: string | null;
  activeTenant: TenantSessionInfo | null;
  availableTenants: TenantMembershipInfo[];
  tenantPickRequired: RequiresTenantPick | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<LoginResponse | RequiresTenantPick>;
  signUp: (payload: SignUpPayload) => Promise<LoginResponse>;
  applyTokens: (payload: BackendTokenResponse) => LoginResponse;
  completeTenantPick: (tenantId: string) => Promise<LoginResponse>;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshMemberships: () => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  changePassword: (request: ChangePasswordRequest) => Promise<void>;
  resetPassword: (request: PasswordResetRequest) => Promise<void>;
  verifyPasswordReset: (request: PasswordResetVerification) => Promise<void>;
  setupTwoFactor: () => Promise<TwoFactorSetup>;
  verifyTwoFactor: (verification: TwoFactorVerification) => Promise<boolean>;
  disableTwoFactor: (code: string) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;

  // Role and permission checking
  roleCheck: RoleCheck;

  // Session management
  extendSession: () => Promise<void>;
  terminateSession: (sessionId: string) => Promise<void>;

  // Internal state management
  setAuth: (auth: Partial<AuthState>) => void;

  /** Most recent raw token response (from login/signUp/completeTenantPick).
   * Cleared on consumption — used by the cross-host SSO handoff so the
   * shared-host page can pass the full payload to the tenant host
   * without an extra round-trip. */
  popLastTokenPayload: () => BackendTokenResponse | null;
}



// Mock departments
const mockDepartments: Department[] = [
  {
    id: 'dept-1',
    name: { en: 'Administration', ar: 'الإدارة' },
    code: 'ADMIN',
    description: { en: 'Hospital Administration', ar: 'إدارة المستشفى' },
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'dept-2',
    name: { en: 'Internal Medicine', ar: 'الطب الباطني' },
    code: 'INTMED',
    description: { en: 'Internal Medicine Department', ar: 'قسم الطب الباطني' },
    headOfDepartment: 'user-2',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'dept-3',
    name: { en: 'Nursing', ar: 'التمريض' },
    code: 'NURS',
    description: { en: 'Nursing Department', ar: 'قسم التمريض' },
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'dept-4',
    name: { en: 'Pharmacy', ar: 'الصيدلية' },
    code: 'PHARM',
    description: { en: 'Pharmacy Department', ar: 'قسم الصيدلية' },
    headOfDepartment: 'user-4',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// Helper function to map backend role name to frontend UserRole enum.
// Exported so other modules (e.g. `userApi.getUsers`) can hydrate
// the `primaryRole` field on user list responses — otherwise only the
// auth-loaded current user has it populated and every role-based
// filter against `useUsers()` results silently returns nothing.
export const mapBackendRoleToUserRole = (roleName: string): UserRole => {
  const roleMap: Record<string, UserRole> = {
    // MVP tenant-visible names
    'Owner': UserRole.OWNER,
    'Clinic Manager': UserRole.CLINIC_MANAGER,
    'Doctor': UserRole.DOCTOR,
    'Nurse': UserRole.NURSE,
    'Receptionist': UserRole.RECEPTIONIST,
    'Billing': UserRole.BILLING,
    'Technician': UserRole.LAB_TECH,
    // Platform-only — should rarely reach tenant UI but maps explicitly.
    'Super Administrator': UserRole.SUPER_ADMIN,
    'IT Support': UserRole.IT_SUPPORT,
    // Legacy backend names — supported for existing tokens until the
    // migration finishes remapping every membership.
    'Administrator': UserRole.OWNER,
    'Admin': UserRole.OWNER,
    'Department Head': UserRole.OWNER,
    'Physician': UserRole.DOCTOR,
    'Radiologist': UserRole.DOCTOR,
    'Pharmacist': UserRole.NURSE,
    'Lab Technician': UserRole.NURSE,
    'Case Manager': UserRole.NURSE,
    'Billing Staff': UserRole.BILLING,
    'Accountant': UserRole.BILLING,
    'Social Worker': UserRole.RECEPTIONIST,
    'Security': UserRole.RECEPTIONIST,
    'Compliance Officer': UserRole.RECEPTIONIST,
  };

  return roleMap[roleName] || UserRole.RECEPTIONIST;
};

// Helper function to map backend permission format (resource:action) to frontend Permission enum
const mapBackendPermissionToFrontend = (permissionName: string): Permission | null => {
  const permissionValues = Object.values(Permission) as string[];
  if (permissionValues.includes(permissionName)) {
    return permissionName as Permission;
  }

  // Backend uses 'medical_records:*' for the records module; the FE enum
  // kept the legacy 'record:*' literal. Translate at the boundary so
  // Owner/Doctor/Nurse don't silently lose RECORD_READ on the FE and have
  // every records-gated sidebar entry filtered out.
  const aliased = permissionName.replace(/^medical_records:/, 'record:');
  if (aliased !== permissionName && permissionValues.includes(aliased)) {
    return aliased as Permission;
  }

  return null;
};

// Role-based permissions mapping
export const rolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.SUPER_ADMIN]: Object.values(Permission), // All permissions

  [UserRole.ADMIN]: [
    Permission.USER_READ, Permission.USER_WRITE, Permission.USER_DELETE,
    Permission.ROLE_READ, Permission.ROLE_WRITE,
    Permission.SYSTEM_READ, Permission.SYSTEM_WRITE,
    Permission.SYSTEM_MONITORING, Permission.SYSTEM_CONFIG, Permission.SYSTEM_BACKUP, Permission.SYSTEM_RESTORE,
    Permission.HOSPITAL_SETTINGS_READ, Permission.HOSPITAL_SETTINGS_WRITE, Permission.BRANDING_CONFIG,
    Permission.AUDIT_READ, Permission.AUDIT_LOGS_READ, Permission.AUDIT_LOGS_EXPORT, Permission.AUDIT_REPORTS_GENERATE,
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.APPOINTMENT_READ, Permission.APPOINTMENT_WRITE,
    Permission.RECORD_READ, Permission.RECORD_WRITE,
    Permission.REPORT_READ, Permission.REPORT_WRITE,
  ],

  [UserRole.DOCTOR]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.RECORD_READ, Permission.RECORD_WRITE,
    Permission.APPOINTMENT_READ, Permission.APPOINTMENT_WRITE,
    Permission.ORDER_READ, Permission.ORDER_WRITE, Permission.ORDER_APPROVE,
    Permission.LAB_ORDER_READ, Permission.LAB_ORDER_WRITE,
    Permission.IMAGING_ORDER_READ, Permission.IMAGING_ORDER_WRITE,
    Permission.PHARMACY_READ, Permission.PHARMACY_WRITE,
    Permission.ADT_READ, Permission.ADT_WRITE, Permission.ADT_ADMIT, Permission.ADT_DISCHARGE,
    Permission.TEMPLATE_READ, Permission.TEMPLATE_WRITE,
    Permission.REPORT_READ,
  ],

  [UserRole.NURSE]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.RECORD_READ, Permission.RECORD_WRITE,
    Permission.APPOINTMENT_READ,
    Permission.ORDER_READ,
    Permission.LAB_ORDER_READ,
    Permission.PHARMACY_READ, // Added pharmacy read permission for nurses (MAR access)
    Permission.ADT_READ, Permission.ADT_WRITE,
    Permission.TEMPLATE_READ,
  ],

  [UserRole.PHARMACIST]: [
    Permission.PATIENT_READ,
    Permission.ORDER_READ, Permission.ORDER_APPROVE,
    Permission.PHARMACY_READ, Permission.PHARMACY_WRITE, Permission.PHARMACY_VERIFY, Permission.PHARMACY_DISPENSE,
    Permission.LAB_ORDER_READ,
    Permission.REPORT_READ,
  ],

  [UserRole.LAB_TECH]: [
    Permission.PATIENT_READ,
    Permission.LAB_ORDER_READ, Permission.LAB_ORDER_WRITE, Permission.LAB_ORDER_RESULT,
    Permission.REPORT_READ,
  ],

  [UserRole.RADIOLOGIST]: [
    Permission.PATIENT_READ,
    Permission.ORDER_READ, Permission.ORDER_APPROVE,
    Permission.IMAGING_ORDER_READ, Permission.IMAGING_ORDER_WRITE,
    Permission.REPORT_READ, Permission.REPORT_WRITE,
  ],

  [UserRole.BILLING_STAFF]: [
    Permission.PATIENT_READ,
    Permission.BILLING_READ, Permission.BILLING_WRITE, Permission.BILLING_PROCESS,
    Permission.REPORT_READ,
  ],

  [UserRole.RECEPTIONIST]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.APPOINTMENT_READ, Permission.APPOINTMENT_WRITE,
    Permission.ADT_READ,
  ],

  [UserRole.SECURITY]: [
    Permission.AUDIT_READ, Permission.AUDIT_LOGS_READ, Permission.AUDIT_REPORTS_GENERATE,
    Permission.USER_READ,
  ],

  [UserRole.IT_SUPPORT]: [
    Permission.SYSTEM_READ, Permission.SYSTEM_WRITE,
    Permission.SYSTEM_MONITORING, Permission.SYSTEM_CONFIG, Permission.SYSTEM_BACKUP, Permission.SYSTEM_RESTORE,
    Permission.USER_READ, Permission.USER_WRITE,
    Permission.AUDIT_READ, Permission.AUDIT_LOGS_READ,
  ],

  [UserRole.DEPARTMENT_HEAD]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.RECORD_READ, Permission.RECORD_WRITE,
    Permission.APPOINTMENT_READ, Permission.APPOINTMENT_WRITE,
    Permission.ORDER_READ, Permission.ORDER_WRITE, Permission.ORDER_APPROVE,
    Permission.REPORT_READ, Permission.REPORT_WRITE,
    Permission.USER_READ,
  ],

  [UserRole.CASE_MANAGER]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.RECORD_READ, Permission.RECORD_WRITE,
    Permission.APPOINTMENT_READ,
    Permission.ADT_READ, Permission.ADT_DISCHARGE,
    Permission.REPORT_READ,
  ],

  [UserRole.SOCIAL_WORKER]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.RECORD_READ, Permission.RECORD_WRITE,
    Permission.APPOINTMENT_READ,
    Permission.REPORT_READ,
  ],

  // MVP roles. The actual authoritative permission set comes from the
  // backend membership; this map exists for offline UI gating only.
  [UserRole.OWNER]: Object.values(Permission), // Full clinic
  [UserRole.BILLING]: [
    Permission.BILLING_READ, Permission.BILLING_WRITE, Permission.BILLING_PROCESS,
    Permission.APPOINTMENT_READ,
    Permission.AUDIT_READ, Permission.AUDIT_LOGS_READ,
    Permission.REPORT_READ,
  ],
  [UserRole.CLINIC_MANAGER]: [
    Permission.PATIENT_READ, Permission.PATIENT_WRITE,
    Permission.APPOINTMENT_READ, Permission.APPOINTMENT_WRITE,
    Permission.RECORD_READ,
    Permission.BILLING_READ, Permission.BILLING_WRITE, Permission.BILLING_PROCESS,
    Permission.USER_READ,
    Permission.AUDIT_READ, Permission.AUDIT_LOGS_READ, Permission.AUDIT_REPORTS_GENERATE,
    Permission.REPORT_READ, Permission.REPORT_WRITE,
  ],
};

// Coalesces concurrent calls (StrictMode double-mount, parallel auth-state
// transitions) into a single /auth/memberships fetch.
let membershipsInFlight: Promise<void> | null = null;

function storeActiveTenantId(tenantId: string | null) {
  if (tenantId) {
    localStorage.setItem('activeTenantId', tenantId);
  } else {
    localStorage.removeItem('activeTenantId');
  }
}

function applyTokenResponse(
  set: (partial: Partial<AuthStore>) => void,
  payload: BackendTokenResponse,
): LoginResponse {
  const { accessToken, refreshToken: backendRefreshToken, user: backendUser, tenant } = payload;

  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', backendRefreshToken);

  const primaryRole = mapBackendRoleToUserRole(backendUser.role.name);
  /* A ROLE IS A SWITCH, NEVER A SECOND ACCOUNT. The permission list is already
     the union the server computed — the guards enforce it there and this is
     only the cache — so the extra names are carried purely so `hasRole` can
     answer for a screen that gates on a role rather than a permission. An
     unknown name maps to null and is dropped: a backend that gains a role this
     client has never heard of must not put `undefined` in the array. */
  const additionalRoles = (backendUser.additionalRoles ?? [])
    .map((n) => mapBackendRoleToUserRole(n))
    .filter((r): r is UserRole => !!r && r !== primaryRole);
  const permissions: Permission[] = backendUser.role.permissions
    .map((p) => mapBackendPermissionToFrontend(p.name))
    .filter((p): p is Permission => p !== null);

  const user: User = {
    id: backendUser.id,
    username: backendUser.username,
    email: backendUser.email,
    firstName: backendUser.firstName,
    lastName: backendUser.lastName,
    displayName: `${backendUser.firstName} ${backendUser.lastName}`,
    primaryRole,
    additionalRoles,
    departmentIds: [],
    status: backendUser.isActive === false ? 'inactive' : 'active',
    isFullTime: true,
    language: 'en',
    timezone: 'UTC',
    lastLogin: backendUser.lastLogin || new Date().toISOString(),
    failedLoginAttempts: 0,
    requirePasswordChange: false,
    twoFactorEnabled: false,
    createdAt: backendUser.createdAt || new Date().toISOString(),
    updatedAt: backendUser.updatedAt || new Date().toISOString(),
    createdBy: 'system',
    lastModifiedBy: 'system',
  };

  const activeTenant = tenant ?? null;
  if (activeTenant) {
    storeActiveTenantId(activeTenant.id);
    rememberLastTenant(activeTenant.id);
  }

  set({
    isAuthenticated: true,
    user,
    token: accessToken,
    refreshToken: backendRefreshToken,
    permissions,
    departments: mockDepartments,
    isLoading: false,
    error: null,
    activeTenantId: activeTenant?.id ?? null,
    activeTenant,
    tenantPickRequired: null,
  });

  return {
    success: true,
    user,
    token: accessToken,
    refreshToken: backendRefreshToken,
    expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    permissions,
    departments: mockDepartments,
  };
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      permissions: [],
      departments: [],
      isLoading: false,
      error: null,

      // Tenant state
      activeTenantId: null,
      activeTenant: null,
      availableTenants: [],
      tenantPickRequired: null,

      // Role checking utilities
      roleCheck: {
        hasRole: (role: UserRole | UserRole[]) => {
          const { user } = get();
          if (!user) return false;

          const roles = Array.isArray(role) ? role : [role];
          return roles.includes(user.primaryRole) ||
            (user.additionalRoles?.some(r => roles.includes(r)) ?? false);
        },

        hasPermission: (permission: Permission | Permission[]) => {
          const { permissions } = get();
          const perms = Array.isArray(permission) ? permission : [permission];
          return perms.every(p => permissions.includes(p));
        },

        hasAnyPermission: (permissions: Permission[]) => {
          const { permissions: userPermissions } = get();
          return permissions.some(p => userPermissions.includes(p));
        },

        hasAllPermissions: (permissions: Permission[]) => {
          const { permissions: userPermissions } = get();
          return permissions.every(p => userPermissions.includes(p));
        },

        canAccessDepartment: (departmentId: string) => {
          const { user } = get();
          return user?.departmentIds.includes(departmentId) ?? false;
        },

        isInDepartment: (departmentId: string) => {
          const { user } = get();
          return user?.departmentIds.includes(departmentId) ?? false;
        }
      },

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null, tenantPickRequired: null });

        try {
          const response = await apiClient.post<BackendLoginEnvelope>(
            '/auth/login',
            {
              username: credentials.username,
              password: credentials.password,
            },
          );
          const envelope = response.data;
          const data =
            'data' in envelope
              ? envelope.data
              : (envelope as unknown as BackendTokenResponse | RequiresTenantPick);

          if ((data as RequiresTenantPick).requiresTenantPick) {
            const pick = data as RequiresTenantPick;
            set({
              isLoading: false,
              tenantPickRequired: pick,
            });
            return pick;
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (set as any)({ _lastTokenPayload: data as BackendTokenResponse });
          return applyTokenResponse(set, data as BackendTokenResponse);
        } catch (error: unknown) {
          const err = error as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string };
          const errorMessage =
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            'Login failed';
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            permissions: [],
            departments: [],
            activeTenantId: null,
            activeTenant: null,
            tenantPickRequired: null,
          });
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          storeActiveTenantId(null);
          throw new Error(errorMessage);
        }
      },

      signUp: async (payload: SignUpPayload) => {
        set({ isLoading: true, error: null, tenantPickRequired: null });
        try {
          const response = await apiClient.post<{ data: BackendTokenResponse } | BackendTokenResponse>(
            '/public/auth/sign-up',
            payload,
          );
          const envelope = response.data as unknown as {
            data?: BackendTokenResponse;
          } & BackendTokenResponse;
          const data = envelope.data ?? envelope;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (set as any)({ _lastTokenPayload: data });
          return applyTokenResponse(set, data);
        } catch (error: unknown) {
          const err = error as {
            response?: { data?: { error?: { message?: string }; message?: string } };
            message?: string;
          };
          const errorMessage =
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            'Sign-up failed';
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            permissions: [],
            departments: [],
            activeTenantId: null,
            activeTenant: null,
            tenantPickRequired: null,
          });
          throw new Error(errorMessage);
        }
      },

      applyTokens: (payload: BackendTokenResponse) => {
        return applyTokenResponse(set, payload);
      },

      popLastTokenPayload: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const internal = get() as any;
        const payload =
          (internal._lastTokenPayload as BackendTokenResponse | null) ?? null;
        if (payload) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (set as any)({ _lastTokenPayload: null });
        }
        return payload;
      },

      completeTenantPick: async (tenantId: string) => {
        const pending = get().tenantPickRequired;
        if (!pending) {
          throw new Error('No pending tenant pick in progress');
        }
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post<{ data: BackendTokenResponse }>(
            '/auth/login/complete',
            {
              pendingLoginToken: pending.pendingLoginToken,
              tenantId,
            },
          );
          const envelope = response.data as unknown as {
            data?: BackendTokenResponse;
          } & BackendTokenResponse;
          const data = envelope.data ?? envelope;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (set as any)({ _lastTokenPayload: data });
          return applyTokenResponse(set, data);
        } catch (error: unknown) {
          const err = error as { message?: string };
          set({ isLoading: false, error: err.message ?? 'Tenant pick failed' });
          throw error;
        }
      },

      switchTenant: async (tenantId: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post<{ data: BackendTokenResponse }>(
            '/auth/switch-tenant',
            { tenantId },
          );
          const envelope = response.data as unknown as {
            data?: BackendTokenResponse;
          } & BackendTokenResponse;
          const data = envelope.data ?? envelope;
          // Cache the freshest tokens so cross-host callers (the
          // tenant switcher hopping into a tenant subdomain, the
          // create-clinic page handing the user into their newly
          // provisioned tenant) can hand them off via the /sso
          // fragment payload without making another round-trip.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (set as any)({ _lastTokenPayload: data });
          applyTokenResponse(set, data);
        } catch (error: unknown) {
          const err = error as { message?: string };
          set({ isLoading: false, error: err.message ?? 'Tenant switch failed' });
          throw error;
        }
      },

      refreshMemberships: async () => {
        if (membershipsInFlight) return membershipsInFlight;

        membershipsInFlight = (async () => {
          try {
            const response = await apiClient.get<{
              data: {
                activeTenantId: string;
                tenants: TenantMembershipInfo[];
              };
            }>('/auth/memberships');
            const envelope = response.data as unknown as {
              data?: { activeTenantId: string; tenants: TenantMembershipInfo[] };
              activeTenantId?: string;
              tenants?: TenantMembershipInfo[];
            };
            const payload = envelope.data ?? {
              activeTenantId: envelope.activeTenantId ?? '',
              tenants: envelope.tenants ?? [],
            };
            const nextTenantId = payload.activeTenantId || get().activeTenantId;
            if (nextTenantId) storeActiveTenantId(nextTenantId);
            set({
              activeTenantId: nextTenantId,
              availableTenants: payload.tenants ?? [],
            });
          } catch (error) {
            console.warn('[auth-store] refreshMemberships failed', error);
          }
        })().finally(() => {
          membershipsInFlight = null;
        });

        return membershipsInFlight;
      },

      logout: async () => {
        set({ isLoading: true });

        try {
          const { refreshToken } = get();

          // Call backend logout API
          if (refreshToken) {
            await apiClient.post('/auth/logout', { refreshToken });
          }

          // Clear tokens from localStorage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          storeActiveTenantId(null);
          clearRememberedTenant();

          // Clear all auth state
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            permissions: [],
            departments: [],
            isLoading: false,
            error: null,
            activeTenantId: null,
            activeTenant: null,
            availableTenants: [],
            tenantPickRequired: null,
          });

        } catch (_error) {
          // Even if logout fails, clear local state
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          storeActiveTenantId(null);

          set({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            permissions: [],
            departments: [],
            isLoading: false,
            error: null,
            activeTenantId: null,
            activeTenant: null,
            availableTenants: [],
            tenantPickRequired: null,
          });
        }
      },

      refreshAuth: async () => {
        // Delegates to apiClient's single-flight refresh rather than
        // POSTing /auth/refresh itself.
        //
        // This used to be its own request, which made two independent
        // refresh paths — this one and the 401 interceptor — race on a
        // rotating refresh token. The winner rotated it; the loser was
        // left holding a revoked token, and its failure ran logout(),
        // kicking the user to /login. Reading localStorage first (as the
        // old comment here described) narrowed the window but could not
        // close it: two simultaneous POSTs still send the same token
        // twice. With /auth/refresh throttled at 10/min, a reconnect
        // storm burned the allowance and started 429ing, turning a
        // recoverable race into a logout loop.
        //
        // performTokenRefresh already writes both localStorage keys and
        // emits tokens-refreshed, which this store subscribes to — so
        // the state update that applyTokenResponse used to do still
        // happens, just once and from one place.
        const refreshToken =
          localStorage.getItem('refreshToken') ?? get().refreshToken;
        if (!refreshToken) return;

        set({ isLoading: true });

        try {
          await performTokenRefresh();
        } catch (_error) {
          get().logout();
        } finally {
          set({ isLoading: false });
        }
      },

      changePassword: async (request: ChangePasswordRequest) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));

          // In production, validate current password and update
          if (request.newPassword !== request.confirmPassword) {
            throw new Error('New passwords do not match');
          }

          if (request.newPassword.length < 8) {
            throw new Error('Password must be at least 8 characters long');
          }

          // Update user password change timestamp
          const { user } = get();
          if (user) {
            const updatedUser = {
              ...user,
              lastPasswordChange: new Date().toISOString(),
              requirePasswordChange: false
            };
            set({ user: updatedUser });
          }

          set({ isLoading: false });

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Password change failed';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      resetPassword: async (request: PasswordResetRequest) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));

          // In production, send password reset email
          console.log('Password reset email sent to:', request.email);

          set({ isLoading: false });

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      verifyPasswordReset: async (request: PasswordResetVerification) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));

          // In production, validate reset token and update password
          if (request.newPassword !== request.confirmPassword) {
            throw new Error('Passwords do not match');
          }

          set({ isLoading: false });

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Password reset verification failed';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      setupTwoFactor: async (): Promise<TwoFactorSetup> => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));

          // In production, generate TOTP secret and QR code
          const setup: TwoFactorSetup = {
            secret: 'JBSWY3DPEHPK3PXP',
            qrCode: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
            backupCodes: [
              '12345678',
              '87654321',
              '11111111',
              '22222222',
              '33333333'
            ]
          };

          set({ isLoading: false });
          return setup;

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Two-factor setup failed';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      verifyTwoFactor: async (verification: TwoFactorVerification): Promise<boolean> => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));

          // In production, verify TOTP code or backup code
          const isValid = verification.code === '123456' || !!verification.backupCode;

          if (isValid) {
            const { user } = get();
            if (user) {
              const updatedUser = { ...user, twoFactorEnabled: true };
              set({ user: updatedUser });
            }
          }

          set({ isLoading: false });
          return isValid;

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Two-factor verification failed';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      disableTwoFactor: async (code: string) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));

          // In production, verify code and disable 2FA
          if (code !== '123456') {
            throw new Error('Invalid verification code');
          }

          const { user } = get();
          if (user) {
            const updatedUser = { ...user, twoFactorEnabled: false };
            set({ user: updatedUser });
          }

          set({ isLoading: false });

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to disable two-factor authentication';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      extendSession: async () => {
        // Simulate extending session
        const { token } = get();
        if (token) {
          // In production, call API to extend session
          console.log('Session extended');
        }
      },

      terminateSession: async (sessionId: string) => {
        // Simulate terminating a specific session
        console.log('Session terminated:', sessionId);
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setAuth: (auth: Partial<AuthState>) => {
        set(auth);
      }
    }),
    {
      name: 'maie-emr-auth',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        permissions: state.permissions,
        departments: state.departments,
        activeTenantId: state.activeTenantId,
        activeTenant: state.activeTenant,
        availableTenants: state.availableTenants,
      }),
      // After a page reload the store rehydrates `activeTenantId`
      // from this persisted blob, but apiClient reads the standalone
      // `activeTenantId` localStorage key. Mirror them on rehydrate so
      // the very first request after reload carries `X-Tenant-Id`.
      onRehydrateStorage: () => (state) => {
        if (state?.activeTenantId) storeActiveTenantId(state.activeTenantId);
      },
    }
  )
);

// Keep the persisted auth store in sync with token rotations performed by
// the apiClient's 401-retry interceptor. The interceptor writes the new
// access/refresh tokens to the standalone localStorage keys; without this
// listener, the zustand-persisted `token`/`refreshToken` drift behind and
// post-reload `refreshAuth()` / `logout()` would post an already-revoked
// refresh token → user kicked to /login. zustand persists on every
// setState, so this listener is enough to keep both copies aligned.
onTokensRefreshed(({ accessToken, refreshToken }) => {
  useAuthStore.setState({ token: accessToken, refreshToken });
});
